import type { ParcelDiscountPolicy } from "../interfaces/ParcelDiscount";
import type { ParcelProduct } from "../interfaces/ParcelProduct";

export type ParcelLineItem = {
  id: number;
  quantity: number;
  unitPrice: number;
  finalPrice: number;
};

/** 기준 금액·할인율이 하나라도 0 이하면 할인 미사용 */
export function isParcelDiscountEnabled(
  thresholdAmount: number,
  discountRate: number,
): boolean {
  return thresholdAmount > 0 && discountRate > 0;
}

export function createExcludedProductIdSet(
  policy: Pick<ParcelDiscountPolicy, "excludedProducts"> | null | undefined,
): Set<number> {
  const set = new Set<number>();
  for (const p of policy?.excludedProducts ?? []) {
    if (Number.isFinite(p.id)) set.add(p.id);
  }
  return set;
}

/**
 * 장바구니 합계가 기준 이상이면 제외 상품 외 할인율 적용.
 * `discountRate`는 퍼센트(10 = 10%).
 */
export function calculateParcelItems(
  items: ParcelProduct[],
  policy: Pick<
    ParcelDiscountPolicy,
    "thresholdAmount" | "discountRate" | "excludedProducts"
  > | null,
): ParcelLineItem[] {
  const threshold = policy?.thresholdAmount ?? 0;
  const ratePercent = policy?.discountRate ?? 0;
  const enabled = isParcelDiscountEnabled(threshold, ratePercent);
  const excludedIds = createExcludedProductIdSet(policy);
  const subtotal = items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );
  const rateFraction = ratePercent / 100;

  return items.map((item) => {
    const isExcluded = excludedIds.has(item.id);
    const applyDiscount =
      enabled && !isExcluded && subtotal >= threshold;
    const unit = applyDiscount
      ? item.price - item.price * rateFraction
      : item.price;
    return {
      id: item.id,
      quantity: item.quantity,
      unitPrice: item.price,
      finalPrice: unit * item.quantity,
    };
  });
}
